import { Button } from '@/components/ui';
import { ChevronRight, X } from 'lucide-react';
import {
	type TutorialNavHighlightRect,
	useSidebar,
} from '@/context/SidebarContext';
import { useEffect, useMemo, useState } from 'react';

type SiteTutorialStepModalProps = {
	title: string;
	description: string;
	stepLabel: string;
	onNext: () => void;
	onDismiss: () => void;
	desktopAnchorRect?: TutorialNavHighlightRect | null;
};

const SiteTutorialStepModal = ({
	title,
	description,
	stepLabel,
	onNext,
	onDismiss,
	desktopAnchorRect = null,
}: SiteTutorialStepModalProps) => {
	const { isMobile } = useSidebar();
	const [entered, setEntered] = useState(false);
	const [viewport, setViewport] = useState(() => ({
		width: typeof window !== 'undefined' ? window.innerWidth : 1366,
		height: typeof window !== 'undefined' ? window.innerHeight : 768,
	}));

	useEffect(() => {
		let raf = 0;
		raf = window.requestAnimationFrame(() => setEntered(true));
		return () => window.cancelAnimationFrame(raf);
	}, []);

	useEffect(() => {
		const updateViewport = () => {
			setViewport({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		};
		updateViewport();
		window.addEventListener('resize', updateViewport);
		return () => window.removeEventListener('resize', updateViewport);
	}, []);

	const desktopDynamicStyle = useMemo(() => {
		if (isMobile || !desktopAnchorRect) return undefined;

		const gap = 28;
		const safe = 20;
		const topSafe = 88;
		const modalWidth = Math.min(352, viewport.width - safe * 2);
		const modalHeightGuess = 270;
		const clamp = (v: number, min: number, max: number) =>
			Math.max(min, Math.min(max, v));

		const anchorMidY =
			(desktopAnchorRect.top + desktopAnchorRect.bottom) / 2;
		const placeAtMiddleY = () =>
			clamp(
				anchorMidY - modalHeightGuess / 2,
				topSafe,
				viewport.height - modalHeightGuess - safe,
			);

		const spaceRight = viewport.width - desktopAnchorRect.right - safe;
		if (spaceRight >= modalWidth + gap) {
			return {
				left: desktopAnchorRect.right + gap,
				top: placeAtMiddleY(),
				width: modalWidth,
			};
		}

		const spaceLeft = desktopAnchorRect.left - safe;
		if (spaceLeft >= modalWidth + gap) {
			return {
				left: desktopAnchorRect.left - gap - modalWidth,
				top: placeAtMiddleY(),
				width: modalWidth,
			};
		}

		const canPlaceAbove =
			desktopAnchorRect.top - topSafe >= modalHeightGuess + gap;
		if (canPlaceAbove) {
			return {
				left: clamp(
					desktopAnchorRect.left,
					safe,
					viewport.width - modalWidth - safe,
				),
				top: desktopAnchorRect.top - gap - modalHeightGuess,
				width: modalWidth,
			};
		}

		return {
			left: clamp(
				desktopAnchorRect.left,
				safe,
				viewport.width - modalWidth - safe,
			),
			top: clamp(
				desktopAnchorRect.bottom + gap,
				topSafe,
				viewport.height - modalHeightGuess - safe,
			),
			width: modalWidth,
		};
	}, [desktopAnchorRect, isMobile, viewport.height, viewport.width]);

	return (
		<div
			className={
				isMobile
					? `pointer-events-auto fixed inset-x-4 top-[5.25rem] z-[100] rounded-2xl border border-zinc-200 bg-white p-5 shadow-2xl transition-all duration-500 ${
							entered
								? 'translate-y-0 opacity-100'
								: 'translate-y-4 opacity-0'
					  }`
					: 'pointer-events-auto fixed z-[100] rounded-2xl border border-zinc-200 bg-white p-5 shadow-2xl transition-all duration-500'
			}
			style={
				isMobile
					? undefined
					: {
							left: 'calc(16rem + 1rem)',
							top: '6rem',
							width: 'min(100vw - 18rem - 2rem, 22rem)',
							maxHeight: 'calc(100vh - 7rem)',
							overflowY: 'auto',
							transform: entered
								? 'translateY(0px)'
								: 'translateY(16px)',
							opacity: entered ? 1 : 0,
							...desktopDynamicStyle,
					  }
			}
			role="dialog"
			aria-label={title}
		>
			<div className="mb-3 flex items-start justify-between gap-3">
				<h2 className="text-lg font-black tracking-tight text-zinc-900">
					{title}
				</h2>
				<button
					type="button"
					onClick={onDismiss}
					className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
					aria-label="Fechar"
				>
					<X className="size-5" />
				</button>
			</div>
			<p className="text-sm leading-relaxed text-zinc-600">
				{description}
			</p>
			<p className="mt-4 text-[10px] font-bold uppercase tracking-[0.14em] text-brand-orange/90">
				{stepLabel}
			</p>
			<Button
				type="button"
				text="Próximo"
				onClick={onNext}
				endIcon={
					<ChevronRight className="size-4 stroke-[2.5] text-white" />
				}
				className="mt-5 w-full justify-center rounded-xl bg-brand-orange py-3 text-sm font-black uppercase tracking-wide text-white hover:bg-brand-orange/90"
			/>
		</div>
	);
};

export { SiteTutorialStepModal };
